/**
 * Guess-feedback sound (no audio assets): two short Web Audio tones, played
 * from a single lazily-created `AudioContext`. Browsers block creating (or
 * auto-resuming) an `AudioContext` before a user gesture; the guess submit
 * that triggers these calls is itself a gesture, so creation here is safe.
 * Every call is wrapped in try/catch: a broken or absent Web Audio API must
 * never break gameplay.
 */

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Play one tone: `type` oscillator from `startFreq` to `endFreq` over `durationMs`. */
function playTone(
  audio: AudioContext,
  {
    type,
    startFreq,
    endFreq,
    startAt,
    durationMs,
    gain,
  }: {
    type: OscillatorType;
    startFreq: number;
    endFreq: number;
    startAt: number;
    durationMs: number;
    gain: number;
  },
): void {
  const osc = audio.createOscillator();
  const amp = audio.createGain();
  osc.type = type;
  const t0 = audio.currentTime + startAt;
  const t1 = t0 + durationMs / 1000;
  osc.frequency.setValueAtTime(startFreq, t0);
  osc.frequency.linearRampToValueAtTime(endFreq, t1);
  amp.gain.setValueAtTime(gain, t0);
  amp.gain.exponentialRampToValueAtTime(0.0001, t1);
  osc.connect(amp);
  amp.connect(audio.destination);
  osc.start(t0);
  osc.stop(t1);
}

/** Short, pleasant ascending chime for a correct guess. */
export function playCorrect(): void {
  try {
    const audio = getContext();
    if (!audio) return;
    playTone(audio, {
      type: "sine",
      startFreq: 880,
      endFreq: 880,
      startAt: 0,
      durationMs: 80,
      gain: 0.08,
    });
    playTone(audio, {
      type: "sine",
      startFreq: 1320,
      endFreq: 1320,
      startAt: 0.08,
      durationMs: 80,
      gain: 0.08,
    });
  } catch {
    // Audio failure must never break gameplay.
  }
}

/** Short, low descending buzz for a wrong guess. */
export function playWrong(): void {
  try {
    const audio = getContext();
    if (!audio) return;
    playTone(audio, {
      type: "triangle",
      startFreq: 220,
      endFreq: 160,
      startAt: 0,
      durationMs: 150,
      gain: 0.08,
    });
  } catch {
    // Audio failure must never break gameplay.
  }
}
